import React, { useEffect, useMemo, useState } from "react";
import { isEqual, omit } from "lodash";
import {
  OnboardingStageWorkflow,
  OnboardingStageWorkflowItem
} from "@/pages/settings/components/contractsSettings/types";
import WorkflowDetailPanelFooter from "@/components/shared/WorkflowDetailPanelFooter";
import WorkflowDetailPanelHeader from "@/components/shared/WorkflowDetailPanelHeader";
import Skeleton from "@/components/ui/skeleton";
import IconContainer from "@/components/ui/icon-container";
import { Guardrails, GuardrailsPositive } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import OnboardingWorkflowItem from "./OnboardingWorkflowItem";
import Badge from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { OnboardingWorkflowResponseValue, WorkflowScoreStatus } from "../types";
import { isAnswered, resolveWorkflowScoreStatus } from "../scoreStatus";
import { useWorkflowScoreStatuses } from "@/entities/settings/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  deserializeResponses,
  serializeResponse,
  useSaveWorkflowResponses,
  useWorkflowResponses,
  WorkflowResponsesMap
} from "@/pages/contracts/hooks";
import { isPublishedOnboardingItem } from "@/pages/settings/components/contractsSettings/onboardingStudio/workflow/helpers";

interface Props {
  contractId: string;
  workflow: OnboardingStageWorkflow;
}

const OnboardingWorkflowDetailPanel: React.FC<Props> = ({ contractId, workflow }) => {
  const { t } = useTranslation();
  const items = useMemo<OnboardingStageWorkflowItem[]>(
    () =>
      (workflow.items ?? []).filter(isPublishedOnboardingItem).sort(
        (a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0)
      ),
    [workflow.items]
  );

  const { data: savedData, isLoading } = useWorkflowResponses(contractId, workflow.id);
  const saveResponses = useSaveWorkflowResponses();
  const { enabled: isAutofillEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10893_CONTRACT_ONBOARDING_AUTOFILL
  );

  // Server-derived baseline: recomputes synchronously when savedData arrives —
  // no extra render cycle like useEffect would cause.
  const serverResponses = useMemo<Record<string, OnboardingWorkflowResponseValue>>(
    () =>
      deserializeResponses(
        savedData?.responses as WorkflowResponsesMap | undefined
      ) as Record<string, OnboardingWorkflowResponseValue>,
    [savedData]
  );

  // Pending user edits layered on top of server responses.
  // Reset when workflow changes so stale edits don't bleed across workflows.
  const [pendingChanges, setPendingChanges] = useState<Record<string, OnboardingWorkflowResponseValue>>({});
  useEffect(() => {
    setPendingChanges({});
  }, [workflow.id]);

  // An edit the stored responses already hold is settled, whichever save reported it,
  // so it stops overriding them and a later change to the stored value shows through.
  // Runs on edits too: an unchanged edit or a rollback can equal what is already stored.
  useEffect(() => {
    setPendingChanges((currentChanges) => {
      const settled = Object.keys(currentChanges).filter((id) =>
        isEqual(currentChanges[id], serverResponses[id])
      );
      return settled.length ? omit(currentChanges, settled) : currentChanges;
    });
  }, [serverResponses, pendingChanges]);

  const responses = useMemo(
    () => ({ ...serverResponses, ...pendingChanges }),
    [serverResponses, pendingChanges]
  );

  // An accepted suggestion changed the stored value, so an earlier unsaved edit
  // of that item must stop shadowing it — and must not be posted back.
  const clearPendingChange = (itemId: string) => {
    setPendingChanges((currentChanges) => {
      if (!(itemId in currentChanges)) return currentChanges;
      const { [itemId]: _cleared, ...rest } = currentChanges;
      void _cleared;
      return rest;
    });
  };

  const updateResponse = (itemId: string, next: OnboardingWorkflowResponseValue) => {
    const hadPendingChange = itemId in pendingChanges;
    // Re-sending a stored answer could overwrite a newer one written elsewhere.
    if (!hadPendingChange && isEqual(next, serverResponses[itemId])) return;
    const previousChange = pendingChanges[itemId];
    const updated = { ...pendingChanges, [itemId]: next };
    // Only unsettled edits are sent, so stored answers are never re-sent from a stale cache.
    const serialized: WorkflowResponsesMap = {};
    Object.entries(updated).forEach(([id, val]) => {
      serialized[id] = serializeResponse(val);
    });
    setPendingChanges(updated);
    saveResponses.mutate(
      { pathParams: { contractId, workflowId: workflow.id }, data: { responses: serialized } },
      {
        // Roll back only this edit, and only while it is still unsettled and unchanged.
        onError: () => {
          setPendingChanges((currentChanges) => {
            if (!Object.is(currentChanges[itemId], next)) return currentChanges;
            return hadPendingChange
              ? { ...currentChanges, [itemId]: previousChange }
              : omit(currentChanges, itemId);
          });
        }
      }
    );
  };

  const totalCount = items.length;
  const requiredItems = items.filter((item) => item.isGuardrails);
  const requiredCount = requiredItems.length;
  const answeredCount = items.filter((item) => isAnswered(responses[item.id])).length;
  const answeredRequiredCount = requiredItems.filter((item) =>
    isAnswered(responses[item.id])
  ).length;

  const { data: scoreStatusesData } = useWorkflowScoreStatuses();
  const scoreStatus = resolveWorkflowScoreStatus(
    items,
    responses,
    savedData?.scoreStatus ?? null
  );

  const showGuardrailIcon = workflow.isGuardrails || requiredCount > 0;
  const isPassed =
    scoreStatus === WorkflowScoreStatus.REQUIRED_COMPLETED ||
    scoreStatus === WorkflowScoreStatus.COMPLETED;

  const getStatusName = (id: number): string | null =>
    scoreStatusesData?.scoreStatuses?.find((statusItem) => statusItem.id === id)?.name ?? null;

  let statusBadge: React.ReactNode = null;
  if (scoreStatus === WorkflowScoreStatus.REQUIRED_COMPLETED || scoreStatus === WorkflowScoreStatus.COMPLETED) {
    const name =
      getStatusName(scoreStatus) ??
      t(
        scoreStatus === WorkflowScoreStatus.COMPLETED
          ? "contracts.detailedView.onboarding.statusCompleted"
          : "contracts.detailedView.onboarding.statusRequiredCompleted"
      );
    statusBadge = (
      <Badge
        text={name}
        textVariant="smBody"
        className="border-none gap-0 rounded-md h-7 whitespace-nowrap px-2 py-1 bg-[rgba(var(--surface-chips-positive-rgb),0.18)]"
        textClassName="text-charts-positive"
      />
    );
  } else if (scoreStatus === WorkflowScoreStatus.IN_PROGRESS) {
    const name =
      getStatusName(scoreStatus) ??
      t("contracts.detailedView.onboarding.statusInProgress");
    statusBadge = (
      <Badge
        text={name}
        textVariant="smBody"
        className="border-none gap-0 rounded-md h-7 whitespace-nowrap px-2 py-1 bg-[rgba(var(--surface-chips-natural-rgb),0.20)]"
        textClassName="text-link"
      />
    );
  }

  return (
    <div
      className="w-full h-full flex flex-col"
      data-test={`onboarding-workflow-detail-panel-${workflow.id}`}
    >
      <WorkflowDetailPanelHeader
        title={workflow.name}
        description={workflow.description}
        rightSlot={
          <>
            {statusBadge}
            {showGuardrailIcon && (
              <IconContainer
                data-test={`guardrail-workflow-${workflow.id}`}
                size="7"
                color={isPassed ? "positive" : "warning"}
                className={cn(
                  "rounded-md",
                  isPassed ? "bg-badges-positive" : "bg-badges-warning"
                )}
              >
                {isPassed ? <GuardrailsPositive /> : <Guardrails />}
              </IconContainer>
            )}
          </>
        }
      />
      <div className="p-8 h-full overflow-y-auto border-b border-border-border1">
        {isLoading ? (
          <div className="flex flex-col gap-8">
            {items.map((item) => (
              <div key={item.id} className="flex flex-col gap-2">
                <Skeleton className="w-48 h-4" />
                <Skeleton className="w-full h-10" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {items.map((item) => (
              <OnboardingWorkflowItem
                key={item.id}
                contractId={contractId}
                item={item}
                value={responses[item.id]}
                onChange={(next) => updateResponse(item.id, next)}
                details={
                  isAutofillEnabled ? savedData?.details?.[item.id] : undefined
                }
                onSuggestionResolved={(action) => {
                  if (action === "approve") clearPendingChange(item.id);
                }}
              />
            ))}
          </div>
        )}
      </div>
      <WorkflowDetailPanelFooter
        translationPrefix="contracts.detailedView.onboarding.workflowFooter"
        answeredCount={answeredCount}
        answeredRequiredCount={answeredRequiredCount}
        requiredCount={requiredCount}
        totalCount={totalCount}
      />
    </div>
  );
};

export default OnboardingWorkflowDetailPanel;
